import { Config } from '@langchain/langgraph-sdk';

import { useEffect, useMemo } from 'react';

import { useGraphAssistant } from '../../api/assistants';
import { useAssistantStore } from '../../store/assistants/assistantsStore';
import { useAssistantDefaultConfigSchema } from './useAssistantDefaultConfigSchema';

export const useInitializeAssistantState = ({
  initAssistantId,
  onAssistantIdChange,
}: {
  initAssistantId: string | undefined;
  onAssistantIdChange: (assistantId: string | null) => void;
}) => {
  const { data: assistant } = useGraphAssistant(initAssistantId);

  const activeAssistantId = useAssistantStore(
    (state) => state.activeAssistantId
  );
  const setActiveAssistantId = useAssistantStore(
    (state) => state.setActiveAssistantId
  );
  const setSelectedAssistantId = useAssistantStore(
    (state) => state.setSelectedAssistantId
  );
  const setCurrentSelectedVersion = useAssistantStore(
    (state) => state.setCurrentSelectedVersion
  );
  const setConfig = useAssistantStore((state) => state.setConfig);

  useEffect(() => {
    if (activeAssistantId || !assistant) return;
    setActiveAssistantId(assistant.assistant_id);
    setSelectedAssistantId(assistant.assistant_id);
    setCurrentSelectedVersion(assistant.version);
  }, [
    assistant,
    activeAssistantId,
    initAssistantId,
    setActiveAssistantId,
    setSelectedAssistantId,
    setCurrentSelectedVersion,
  ]);

  useEffect(() => {
    if (!activeAssistantId) return;
    onAssistantIdChange(activeAssistantId);
  }, [activeAssistantId, onAssistantIdChange]);

  // on unmmount to set activeAssistantId to undefined
  useEffect(() => {
    return () => {
      setActiveAssistantId(undefined);
    };
  }, [setActiveAssistantId]);

  const { defaultConfigSchema } = useAssistantDefaultConfigSchema(
    assistant?.assistant_id
  );
  const initialConfig = useMemo(() => {
    const defaultConfig = { configurable: defaultConfigSchema };

    if (!assistant) return defaultConfig;
    const assistantConfig = assistant.config;

    const config = () => {
      if (assistantConfig?.configurable) {
        return assistantConfig;
      }
      return defaultConfig;
    };
    const result: Config = { ...config() };
    delete result.configurable?.thread_id;
    delete result.configurable?.thread_ts;
    delete result.configurable?.checkpoint_id;
    return result;
  }, [assistant, defaultConfigSchema]);

  useEffect(() => {
    if (initialConfig) {
      setConfig(initialConfig);
    }
  }, [initialConfig, setConfig]);
};
